import type { IconType } from 'react-icons';
import { BiCalendar, BiHomeAlt2 } from 'react-icons/bi';

import type { StatusTone } from '@/components';
import type { Berth, BerthPricePeriod, BerthType } from '@/store/vessel-marinas/vessel-marinas.dto';
import { formatEventDateRange } from '@/utils/formatEventDateRange';

/** The vessel service caps a berth's title here. */
export const TITLE_MAX = 100;

export const BERTH_TYPE_LABEL: Record<BerthType, string> = {
  permanent: 'Permanent',
  temporary: 'Temporary',
};

/** The type card's glyph, so the pill reads as the card the user picked. */
export const BERTH_TYPE_ICON: Record<BerthType, IconType> = {
  permanent: BiHomeAlt2,
  temporary: BiCalendar,
};

/** Blue, not teal: the Home Port pill beside a Permanent one is already teal. */
export const BERTH_TYPE_TONE: Record<BerthType, StatusTone> = {
  permanent: 'progress',
  temporary: 'neutral',
};

export const PRICE_PERIODS: { label: string; value: BerthPricePeriod }[] = [
  { label: 'per hour', value: 'hour' },
  { label: 'per night', value: 'night' },
  { label: 'per week', value: 'week' },
  { label: 'per month', value: 'month' },
  { label: 'per year', value: 'year' },
];

/** The likeliest billing period for each berth type, until the user picks one. */
export const DEFAULT_PRICE_PERIOD: Record<BerthType, BerthPricePeriod> = {
  permanent: 'month',
  temporary: 'night',
};

export const formatBerthDates = ({ dateFrom, dateTo }: Pick<Berth, 'dateFrom' | 'dateTo'>) =>
  dateFrom && dateTo ? formatEventDateRange(dateFrom, dateTo) : undefined;

export const formatBerthPrice = ({ price, pricePeriod }: Pick<Berth, 'price' | 'pricePeriod'>) => {
  if (!price) return undefined;
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency,
  }).format(Number(price.amount));
  return pricePeriod ? `${amount} / ${pricePeriod}` : amount;
};

/** Permanent berths lead: the home marina is the one most often reached for. */
export const sortBerths = (berths: Berth[]) =>
  [...berths].sort((a, b) => Number(b.type === 'permanent') - Number(a.type === 'permanent'));
